import { z } from 'zod';
import { normalizeMatter } from '../engine/extracts.js';
import { unfenced } from '../lib/unfenced.js';
import {
  ANALYST_PASSAGES_MAX,
  ANALYST_PASSAGE_MAX,
  type AnalysisFunctionRow,
  type AnalysisStyleNote,
} from '../schemas/analysis.js';
import type { AnalystLevel } from '../schemas/analyst.js';
import {
  NOYAU_CODES,
  NOYAU_JUSTIFICATION_MAX,
  type NoyauCode,
  noyauCodeSchema,
} from '../schemas/noyau.js';

/**
 * WHAT THE TWO PASSES ANSWER (analyste.md §5.2, §7.3, issue #196), and the check
 * the code makes of it before anything believes it.
 *
 * A model's output is an external input (the repository's boundary rule): each
 * answer is read by a Zod schema, an answer that does not read - or that reads but
 * does not match what was asked - is UNUSABLE, retried once and then a failure
 * (§5.3, §7.5). Two layers, as the ranger's answer has them:
 *
 * - the SHAPE (`functionsAnswerSchema`, `styleAnswerSchema`) - what the contract
 *   written in the system says, whatever the session;
 * - the CONTEXT (`functionsAnswerSchemaFor`, `styleAnswerSchemaFor`) - what was
 *   ASKED of this call: exactly the objectives that were served, exactly the
 *   candidates that were named. A missing, an extra, an unknown or a repeated one
 *   makes the answer unusable.
 *
 * Neither schema is `.strict()`, like every shape a model produces: a model that
 * adds a key must not cost a determination, a model that drops a note must.
 */

/* ----------------------------------------------------------------- the first pass */

/** A whole percentage - a part, a note. */
const percent = z.number().int().min(0).max(100);

/** One sentence of justification, bounded like the notes of the analyst before it (N1). */
const sentence = z.string().trim().min(1).max(NOYAU_JUSTIFICATION_MAX);

/**
 * The three parts of one objective (§5.2, K6): three whole numbers from 0 to 100
 * whose sum is EXACTLY 100. A sum of 95 or 105 is not normalised: the answer is
 * unusable and is asked again.
 */
export const functionsSharesSchema = z
  .object({ S: percent, G: percent, P: percent })
  .refine((shares) => shares.S + shares.G + shares.P === 100, {
    message: 'The three parts of an objective sum to exactly 100.',
  });

/** One objective of the first pass's answer: its identifier, its three parts, its sentence. */
export const functionsAnswerObjectiveSchema = z.object({
  objectiveId: z.string().min(1),
  shares: functionsSharesSchema,
  justification: sentence,
});

/** The JSON the first pass returns (§5.2) - the contract of `functionsSystemParts` written as a schema. */
export const functionsAnswerSchema = z.object({
  objectives: z.array(functionsAnswerObjectiveSchema),
});
export type FunctionsAnswer = z.infer<typeof functionsAnswerSchema>;

/**
 * The answer of the first pass against what it was ASKED (§5.2): one entry for
 * each objective served, once and only once - an objective that is missing,
 * unknown or answered twice makes the answer unusable.
 */
export function functionsAnswerSchemaFor(expectedObjectiveIds: readonly string[]) {
  const expected = new Set(expectedObjectiveIds);
  return functionsAnswerSchema.superRefine((answer, ctx) => {
    const seen = new Set<string>();
    answer.objectives.forEach((objective, index) => {
      const path = ['objectives', index, 'objectiveId'];
      if (!expected.has(objective.objectiveId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: `The objective ${objective.objectiveId} was not served.`,
        });
      } else if (seen.has(objective.objectiveId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: `The objective ${objective.objectiveId} is answered twice.`,
        });
      }
      seen.add(objective.objectiveId);
    });
    for (const id of expected) {
      if (seen.has(id)) continue;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['objectives'],
        message: `The objective ${id} is missing from the answer.`,
      });
    }
  });
}

/** Reads the raw text of a call as JSON, a ``` fence forgiven (`unfenced`); null when it is not JSON. */
function parsedJson(raw: string): unknown {
  const text = unfenced(raw);
  if (text === '') return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * The answer of the first pass, parsed against the objectives it was served - or
 * null, an answer of the wrong shape (§5.3): retried once, then the determination
 * settles `failed`. Null and never a throw, because an unusable answer is a normal
 * outcome of a call and not an error of the session.
 */
export function readFunctionsAnswer(
  raw: string,
  expectedObjectiveIds: readonly string[],
): FunctionsAnswer | null {
  const parsed = functionsAnswerSchemaFor(expectedObjectiveIds).safeParse(parsedJson(raw));
  return parsed.success ? parsed.data : null;
}

/**
 * What the first pass was SERVED of one objective, for what the code keeps of it:
 * its identifier, its name at the frozen protocol, and the level the code knew it
 * at.
 */
export type ServedObjective = {
  readonly objectiveId: string;
  readonly name: string;
  readonly level: AnalystLevel;
};

/**
 * The first pass as the analysis keeps it (§10): one row per objective SERVED, in
 * the order of the protocol, each with its name, its level, the parts and the
 * sentence the pass answered. `answer` has been read against `served`
 * (`readFunctionsAnswer`), so every row finds its parts; an objective with no
 * answer - which that reading forbids - throws rather than inventing parts.
 */
export function functionRowsOf(
  served: readonly ServedObjective[],
  answer: FunctionsAnswer,
): AnalysisFunctionRow[] {
  const answered = new Map(
    answer.objectives.map((objective) => [objective.objectiveId, objective]),
  );
  return served.map((objective) => {
    const given = answered.get(objective.objectiveId);
    if (given === undefined) {
      throw new Error(`The first pass has no answer for the objective ${objective.objectiveId}.`);
    }
    return {
      objectiveId: objective.objectiveId,
      name: objective.name,
      level: objective.level,
      shares: given.shares,
      justification: given.justification,
    };
  });
}

/* ---------------------------------------------------------------- the second pass */

/** A passage a model quotes: a non-empty text, bounded - the check below decides whether it is the person's. */
const passage = z.string().trim().min(1).max(ANALYST_PASSAGE_MAX);

/** One candidate of the second pass's answer (§7.3): its triplet, its style note, its passages, its sentence. */
export const styleAnswerNoteSchema = z.object({
  noyau: noyauCodeSchema,
  score: percent,
  supporting: z.array(passage).max(ANALYST_PASSAGES_MAX).default([]),
  contradicting: z.array(passage).max(ANALYST_PASSAGES_MAX).default([]),
  justification: sentence,
});
export type StyleAnswerNote = z.infer<typeof styleAnswerNoteSchema>;

/**
 * The JSON the second pass returns (§7.3): a whole note from 0 to 100 per
 * candidate, at most eight passages for and eight against, each copied word for
 * word from the person's answers, and a sentence.
 */
export const styleAnswerSchema = z.object({ styles: z.array(styleAnswerNoteSchema) });
export type StyleAnswer = z.infer<typeof styleAnswerSchema>;

/**
 * The answer of the second pass against the candidates it was ASKED about (§7.3):
 * every candidate once and only once - a missing candidate, a noyau that is not a
 * candidate, a noyau noted twice, make the answer unusable. A note out of bounds
 * fails the shape.
 */
export function styleAnswerSchemaFor(candidates: readonly NoyauCode[]) {
  const expected = new Set(candidates);
  return styleAnswerSchema.superRefine((answer, ctx) => {
    const seen = new Set<NoyauCode>();
    answer.styles.forEach((note, index) => {
      const path = ['styles', index, 'noyau'];
      if (!expected.has(note.noyau)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: `The noyau ${note.noyau} is not a candidate: it is not noted.`,
        });
      } else if (seen.has(note.noyau)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: `The noyau ${note.noyau} is noted twice.`,
        });
      }
      seen.add(note.noyau);
    });
    for (const noyau of expected) {
      if (seen.has(noyau)) continue;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['styles'],
        message: `The candidate ${noyau} is missing from the answer.`,
      });
    }
  });
}

/** The answer of the second pass, parsed against its candidates - or null (§7.5): retried once, then « style absent ». */
export function readStyleAnswer(raw: string, candidates: readonly NoyauCode[]): StyleAnswer | null {
  const parsed = styleAnswerSchemaFor(candidates).safeParse(parsedJson(raw));
  return parsed.success ? parsed.data : null;
}

/* ------------------------------------------------------------- the passages checked */

/** One answer of the person, as the second pass was served it: the number of its turn, and its verbatim. */
export type StyleServedAnswer = {
  /** The number Sessions prints: the turn's `index` plus one. */
  readonly number: number;
  readonly reply: string;
};

/**
 * The form a passage and an answer are compared on (§7.4, K7), never the form
 * that is stored: Unicode normalised (compatibility forms folded, an accented
 * letter written with a combining mark read as the letter), case ignored, the
 * apostrophes - straight or typographic - folded to one, the blanks collapsed.
 *
 * It is `normalizeMatter` of the scribe's literality check with the case and the
 * Unicode form folded on top: the scribe's extracts keep their case (a stricter
 * check of what a person said), a quoted passage of a style is a pointer to words
 * the person said, and a model that lowercases a quote has not invented it.
 *
 * The apostrophes are folded BEFORE the Unicode form, and once more after it.
 * Before, because NFKC reads the isolated acute accent U+00B4 as a blank and a
 * combining acute: a quote written « j´ai » would then no longer hold an
 * apostrophe to fold, and would be set aside against a plain « j'ai ». After,
 * because NFKC brings some forms back to an apostrophe itself (the fullwidth
 * grave accent reads as a backtick). The second pass changes nothing else: it is
 * idempotent.
 */
export function normalizedPassage(text: string): string {
  return normalizeMatter(normalizeMatter(text).normalize('NFKC').toLowerCase());
}

/**
 * CHECK the passages of the second pass (§7.4, K7; « la double vérification de
 * Patrick »): each passage a model quotes is looked for in the answers of the
 * person, on the form `normalizedPassage` gives, inside ONE answer - a quote
 * running across two is not one the person said. A passage that is not found is
 * set aside and COUNTED, never a failure: the note stays, and the parcours says
 * how many passages were set aside.
 *
 * The notes come back in the order of the table, whatever order the model gave
 * them in, each with the name of its style in the version of Méthode read
 * (`styleNameOf`), its passages found - collapsed to one line, in the order
 * given - and the ones that were not.
 */
export function verifiedStyleNotes(
  answer: StyleAnswer,
  answers: readonly StyleServedAnswer[],
  styleNameOf: (noyau: NoyauCode) => string,
): AnalysisStyleNote[] {
  const haystacks = answers.map((served) => normalizedPassage(served.reply));
  const found = (text: string): boolean => {
    const needle = normalizedPassage(text);
    return needle !== '' && haystacks.some((haystack) => haystack.includes(needle));
  };
  const oneLine = (text: string): string => text.replace(/\s+/g, ' ').trim();

  return NOYAU_CODES.flatMap((noyau) => {
    const note = answer.styles.find((entry) => entry.noyau === noyau);
    if (note === undefined) return [];
    const discarded: AnalysisStyleNote['discarded'] = [];
    const kept = (side: 'supporting' | 'contradicting'): string[] => {
      const retained: string[] = [];
      for (const text of note[side]) {
        if (found(text)) retained.push(oneLine(text));
        else discarded.push({ side, text: oneLine(text) });
      }
      return retained;
    };
    const supporting = kept('supporting');
    const contradicting = kept('contradicting');
    return [
      {
        noyau,
        styleName: styleNameOf(noyau),
        score: note.score,
        supporting,
        contradicting,
        discarded,
        discardedCount: discarded.length,
        justification: note.justification,
      },
    ];
  });
}
